import React from 'react';

const UNIT_SIZES = ['150g','250g','350g','500g','600g','1KG','1.5KG','2KG','2.5KG','5KG','10KG','12KG','13.6KG','15KG','20KG','4 x 100g'];

const fieldStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  marginBottom: 16,
};

const labelStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 600,
  color: '#6d7175',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '7px 10px',
  border: '1px solid #e1e3e5',
  borderRadius: 6,
  fontSize: '0.85rem',
  color: '#202223',
  background: '#fff',
  outline: 'none',
};

const errorStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  color: '#ec174f',
  marginTop: 2,
};

const Field = ({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) => (
  <div style={fieldStyle}>
    <label style={labelStyle}>{label}</label>
    {children}
    {error && <span style={errorStyle}>{error}</span>}
  </div>
);

export interface ProductFormValues {
  name: string;
  description: string;
  benefits: string;
  didYouKnow: string;
  productCode: string;
  origin: string;
  unitSize: string;
  category: string;
  subCategory: string;
  price: string;
  inStock: string;
}

interface Props {
  values: ProductFormValues;
  errors: any;
  productImageUrl: string;
  didYouKnowImageUrl: string;
  onChange: (e: any) => void;
  onImageSelect: (e: any) => void;
  onSubmit: (e: any) => void;
  onBack: () => void;
  submitLabel: string;
  loading: boolean;
  extraActions?: React.ReactNode;
}

const ProductForm = ({
  values,
  errors,
  productImageUrl,
  didYouKnowImageUrl,
  onChange,
  onImageSelect,
  onSubmit,
  onBack,
  submitLabel,
  loading,
  extraActions,
}: Props) => (
  <form encType="multipart/form-data" onSubmit={onSubmit}>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
      <Field label="Product name" error={errors.name}>
        <input style={inputStyle} placeholder="e.g. Organic Blueberries" onChange={onChange} value={values.name} name="name" className={errors.name ? 'is-invalid' : ''} />
      </Field>

      <Field label="Price (AUD)" error={errors.price}>
        <input style={inputStyle} type="number" min="0" step="0.01" placeholder="0.00" onChange={onChange} value={values.price} name="price" className={errors.price ? 'is-invalid' : ''} />
      </Field>

      <Field label="Product code" error={errors.productCode}>
        <input style={inputStyle} placeholder="e.g. SKU-001" onChange={onChange} value={values.productCode} name="productCode" className={errors.productCode ? 'is-invalid' : ''} />
      </Field>

      <Field label="Origin" error={errors.origin}>
        <input style={inputStyle} placeholder="e.g. Australia" onChange={onChange} value={values.origin} name="origin" className={errors.origin ? 'is-invalid' : ''} />
      </Field>

      <Field label="Unit size" error={errors.unitSize}>
        <select style={inputStyle} onChange={onChange} value={values.unitSize} name="unitSize" className={errors.unitSize ? 'is-invalid' : ''}>
          <option hidden value="">Select size</option>
          {UNIT_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </Field>

      <Field label="Category" error={errors.category}>
        <select style={inputStyle} onChange={onChange} value={values.category} name="category" className={errors.category ? 'is-invalid' : ''}>
          <option hidden value="">Select category</option>
          <option value="organic">Organic</option>
          <option value="australian">Australian</option>
          <option value="conventional">Conventional</option>
          <option value="puree">Puree</option>
        </select>
      </Field>

      <Field label="Sub-category" error={errors.subCategory}>
        <select style={inputStyle} onChange={onChange} value={values.subCategory} name="subCategory" className={errors.subCategory ? 'is-invalid' : ''}>
          <option hidden value="">Select sub-category</option>
          <option value="fruits">Fruit</option>
          <option value="vegetables">Vegetable</option>
        </select>
      </Field>

      <Field label="Stock level" error={errors.inStock}>
        <select style={inputStyle} onChange={onChange} value={values.inStock} name="inStock" className={errors.inStock ? 'is-invalid' : ''}>
          <option value="inStock">In stock</option>
          <option value="outofStock">Out of stock</option>
        </select>
      </Field>
    </div>

    <Field label="Description" error={errors.description}>
      <textarea style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} placeholder="Product description" onChange={onChange} value={values.description} name="description" className={errors.description ? 'is-invalid' : ''} />
    </Field>

    <Field label="Benefits" error={errors.benefits}>
      <textarea style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} placeholder="Product benefits" onChange={onChange} value={values.benefits} name="benefits" className={errors.benefits ? 'is-invalid' : ''} />
    </Field>

    <Field label="Did you know?" error={errors.didYouKnow}>
      <textarea style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} placeholder="Interesting fact" onChange={onChange} value={values.didYouKnow} name="didYouKnow" className={errors.didYouKnow ? 'is-invalid' : ''} />
    </Field>

    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
      {(['image', 'didYouKnowImage'] as const).map((field) => {
        const url = field === 'image' ? productImageUrl : didYouKnowImageUrl;
        const hasImage = url && url !== 'undefined';
        return (
          <Field key={field} label={field === 'image' ? 'Product image' : 'Did you know image'} error={errors[field]}>
            <label
              htmlFor={field}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: 160,
                border: '2px dashed #c9cccf',
                borderRadius: 8,
                cursor: 'pointer',
                overflow: 'hidden',
                position: 'relative',
                background: '#fafafa',
                gap: 8,
              }}
            >
              {hasImage && <img src={url} alt={field} style={{ maxWidth: '100%', maxHeight: 110 }} />}
              <span style={{ fontSize: '0.75rem', color: '#6d7175', fontWeight: 500 }}>
                <i className="fas fa-upload" style={{ marginRight: 6 }} />
                {hasImage ? 'Click to change' : 'Click to upload'}
              </span>
            </label>
            <input type="file" name={field} id={field} onChange={onImageSelect} style={{ display: 'none' }} />
          </Field>
        );
      })}
    </div>

    <div style={{ display: 'flex', gap: 8, marginTop: 24 }}>
      <button
        type="submit"
        className="admin-dropdown-trigger"
        disabled={loading}
        style={{ background: '#ec174f', color: '#fff', borderColor: '#ec174f', opacity: loading ? 0.6 : 1 }}
      >
        {loading ? 'Please wait…' : submitLabel}
      </button>
      {extraActions}
      <button type="button" className="admin-dropdown-trigger" onClick={onBack}>
        Back to products
      </button>
    </div>
  </form>
);

export default ProductForm;
